import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { CaseAppealListRow } from "../../../data-model/caseAppealsTable";
import { FilterValues } from "../../../data-model/filterValues";
import { TableNames } from "../../../enums/tableNames";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { buildAppealListPayload } from "../utils/buildAppealListPayload";
import { CaseAppealsTableState } from "./useCaseAppealsTableData";
import { AppealListFilterPayload, useFilterPayload } from "./useFilterPayload";

export interface UseNextPageDataFetchReturn {
  endIndex: number;
  setEndIndex: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Loads additional appeal list pages when the user paginates near the end of loaded rows.
 *
 * @param validFilters - Applied table filters from Redux
 * @param searchTerm - Current search text, so paged requests keep the search applied
 * @param initialDataFetchingExecuted - Whether the first page has loaded
 * @param caseAppealsState - Current table fetch state
 * @param setCaseAppealsState - Table loading/error/data state setter
 * @param setTableData - Raw API rows setter
 * @param pageSize - Page size sent as `limit` to the appeal list API
 * @returns End index tracker used by the table pagination callback
 */
export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  caseAppealsState: CaseAppealsTableState,
  setCaseAppealsState: React.Dispatch<
    React.SetStateAction<CaseAppealsTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<CaseAppealListRow[]>>,
  pageSize: number = 50,
): UseNextPageDataFetchReturn => {
  const { setTableDataForContext } = useTablesContext();
  const [endIndex, setEndIndex] = useState<number>(0);

  const lastRequestedPageRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);

  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    pageSize,
  );

  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setCaseAppealsStateRef = useRef(setCaseAppealsState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setCaseAppealsStateRef.current = setCaseAppealsState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setCaseAppealsState,
    setTableData,
    setTableDataForContext,
  ]);

  const validFiltersKey = JSON.stringify(validFilters);
  useEffect(() => {
    lastRequestedPageRef.current = 0;
  }, [validFiltersKey, searchTerm]);

  const shouldFetchMoreData = useMemo(() => {
    const currentPageRowCount = caseAppealsState.data?.row_count ?? 0;
    const currentPage = caseAppealsState.data?.page ?? 1;
    const totalPages = caseAppealsState.data?.total_pages ?? 1;
    const nextPageToFetch = currentPage + 1;

    const previousPagesRows = (currentPage - 1) * pageSize;
    const cumulativeRowsLoaded = previousPagesRows + currentPageRowCount;

    return (
      endIndex >= cumulativeRowsLoaded - 1 &&
      currentPage < totalPages &&
      !caseAppealsState.loading &&
      !caseAppealsState.showInlineLoader &&
      nextPageToFetch > lastRequestedPageRef.current
    );
  }, [
    endIndex,
    caseAppealsState.data?.row_count,
    caseAppealsState.data?.page,
    caseAppealsState.data?.total_pages,
    caseAppealsState.loading,
    caseAppealsState.showInlineLoader,
    pageSize,
  ]);

  const payloadFinal = useMemo<AppealListFilterPayload>(() => {
    if (filterPayload === null) {
      return buildAppealListPayload(validFilters, searchTerm, pageSize);
    }
    return filterPayload;
  }, [filterPayload, validFilters, searchTerm, pageSize]);

  const payloadFinalWithPage = useMemo<AppealListFilterPayload>(() => {
    return {
      ...payloadFinal,
      page: (caseAppealsState.data?.page ?? 0) + 1,
    };
  }, [payloadFinal, caseAppealsState.data?.page]);

  const currentPage = caseAppealsState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    if (!isMountedRef.current) return;

    const nextPage = currentPage + 1;
    lastRequestedPageRef.current = nextPage;

    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      setCaseAppealsStateRef.current,
      setTableDataRef.current,
      isMountedRef,
    );
    setTableDataForContextRef.current(TableNames.ONCOLOGY_CASE_APPEALS_ORDERS, {
      lastBatchFetched: nextPage,
    });
  }, [payloadFinalWithPage, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
